import { type ReactNode, useEffect, useRef } from "react";

interface Props {
  children: ReactNode;
  display: boolean;
  onClosed?: () => void;
}

/**
 * Displays a modal dialog whenever mounted and display is true
 *
 * @prop children - (Required) the content to display in the modal
 * @prop display - (Required) when true displays the modal
 * @prop onClosed - (Optional) fired whenever the modal is to close
 */
const ModalDialog = ({ children, display, onClosed }: Props) => {
  const modalOverlay = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClosed?.();
    }
  };

  useEffect(() => {
    if (display) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", handleKeyDown);
      modalOverlay.current?.addEventListener("click", handleDocumentClicked);
    }

    return () => {
      document.body.style.overflow = "auto";
      document.removeEventListener("keydown", handleKeyDown);
      modalOverlay.current?.removeEventListener("click", handleDocumentClicked);
    };
  }, [display]);

  const handleDocumentClicked = () => {
    if (onClosed) onClosed();
  };

  return (
    <div id="modal-dialog-component">
      {display && (
        <>
          <div className="modal-content">{children}</div>
          <div className="modal-overlay" ref={modalOverlay} />
        </>
      )}
    </div>
  );
};

export { ModalDialog };
